import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ZoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, ZoomPlugin);

const GRAPH_TITLES = {
  totalVolume: '総ボリューム (kg)',
  maxEstimated1RM: '最大推定1RM (kg)',
};

const formatDateLabel = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const computeValue = (historyEntry, mode) => {
  const { sets } = historyEntry;
  if (!sets || sets.length === 0) {
    return 0;
  }
  if (mode === 'maxEstimated1RM') {
    let max = 0;
    sets.forEach((set) => {
      const weight = Number(set.weight);
      const reps = Number(set.reps);
      if (!Number.isFinite(weight) || !Number.isFinite(reps) || weight <= 0 || reps <= 0) {
        return;
      }
      const estimated = weight * Math.pow(reps, 0.1);
      if (estimated > max) {
        max = estimated;
      }
    });
    return max;
  }
  return sets.reduce(
    (total, set) => total + (Number(set.weight) || 0) * (Number(set.reps) || 0),
    0
  );
};

const tooltipLabelFormatter = (label) => label;

const DOT_RADIUS = 4;
const GRAPH_HEIGHT = 260;
const MAX_VISIBLE_POINTS = 10; // 最初に表示する最大データポイント数
export const MOVING_AVERAGE_WINDOW = 3;

export const calculateMovingAverages = (
  values,
  windowSize = MOVING_AVERAGE_WINDOW
) => values.map((_, index) => {
  const start = Math.max(0, index - windowSize + 1);
  const windowValues = values.slice(start, index + 1);
  return windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length;
});

function WorkoutGraph({
  history,
  graphType,
  onSelectSession,
  selectedHistoryIndex,
}) {
  const normalizedType = GRAPH_TITLES[graphType] ? graphType : 'totalVolume';

  const points = useMemo(() => (history || []).map((entry, index) => ({
    index,
    label: formatDateLabel(entry.timestamp),
    value: computeValue(entry, normalizedType),
  })), [history, normalizedType]);

  const { chartData, maxValue, movingAverages } = useMemo(() => {
    const values = points.map((point) => point.value);
    const maxValue = values.length > 0 ? Math.max(...values) : 0;
    const movingAverages = calculateMovingAverages(values);

    const data = {
      labels: points.map((point) => point.label),
      datasets: [
        {
          label: GRAPH_TITLES[normalizedType],
          data: values,
          borderColor: '#4a6cf7',
          backgroundColor: 'rgba(74, 108, 247, 0.12)',
          tension: 0.3,
          pointRadius: DOT_RADIUS,
          pointHoverRadius: DOT_RADIUS + 2,
          pointHitRadius: DOT_RADIUS + 4,
        },
        {
          label: '最大',
          data: new Array(points.length).fill(maxValue),
          borderColor: 'red',
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        },
        {
          label: `移動平均（${MOVING_AVERAGE_WINDOW}回）`,
          data: movingAverages,
          borderColor: 'green',
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        },
      ],
    };
    return { chartData: data, maxValue, movingAverages };
  }, [points, normalizedType]);

  const movingAverageIndex = Number.isInteger(selectedHistoryIndex)
    && selectedHistoryIndex >= 0
    && selectedHistoryIndex < movingAverages.length
    ? selectedHistoryIndex
    : movingAverages.length - 1;
  const selectedMovingAverage = movingAverages[movingAverageIndex];

  // Determine initial x-axis range
  const xMin = Math.max(0, points.length - MAX_VISIBLE_POINTS);
  const xMax = points.length - 1;

  const chartOptions = useMemo(() => {
    const tooltipFormatter = (value) => {
      const rounded = normalizedType === 'maxEstimated1RM' ? 3 : 2;
      return value.toFixed(rounded);
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: true,
        mode: 'nearest',
      },
      scales: {
        y: { beginAtZero: true },
        x: {
          min: xMin, // Display only the last MAX_VISIBLE_POINTS data points initially
          max: xMax,
        },
      },
      plugins: {
        legend: { display: true },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed?.y;
              const formatted = Number.isFinite(value) ? tooltipFormatter(value) : value;
              return `${context.dataset.label}: ${formatted}`;
            },
            title: (items) => {
              const label = items?.[0]?.label;
              return label ? tooltipLabelFormatter(label) : '';
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x', // パンをx軸方向のみに制限
          },
          zoom: {
            wheel: {
              enabled: true, // ホイールでのズームを有効化 (任意)
            },
            pinch: {
              enabled: true // ピンチでのズームを有効化 (モバイル向け)
            },
            mode: 'x', // ズームをx軸方向のみに制限
          },
        },
      },
      onClick: (_, elements) => {
        const first = elements?.[0];
        if (!first) return;
        const pointIndex = first.index;
        if (onSelectSession && Number.isInteger(pointIndex)) {
          onSelectSession(pointIndex);
        }
      },
    };
  }, [xMin, xMax, normalizedType, onSelectSession]);

  const formatStatValue = (value) => {
    const rounded = normalizedType === 'maxEstimated1RM' ? 3 : 2;
    return Number.isFinite(value) ? value.toFixed(rounded) : '-';
  };

  if (!history || history.length === 0) {
    return <div className="graph-placeholder">まだ履歴がありません</div>;
  }

  return (
    <div className="graph-wrapper">
      <div className="graph-title">{GRAPH_TITLES[normalizedType] || 'グラフ'}</div>
      <div style={{ width: '100%', height: GRAPH_HEIGHT }}>
        <Line data={chartData} options={chartOptions} />
      </div>
      <div className="graph-stats">
        <span>最大: {formatStatValue(maxValue)}</span>
        <span>
          移動平均（{MOVING_AVERAGE_WINDOW}回）: {formatStatValue(selectedMovingAverage)}
        </span>
      </div>
    </div>
  );
}

export default WorkoutGraph;
